import { useEffect, useMemo } from 'react';

import { EmptyState, Spinner } from '../components';
import { stripLeadingOrdinal } from '../config/content-types';
import { BookOpenIcon } from '../layout/icons';
import { useQltIztContentStore, useQltTimetableStore } from '../stores/qlt';
import { UnitList } from './panels/LectureLibraryTree';
import { resolvePeriodCourseScope } from './periodLabels';
import { periodSuggestions } from './suggestions';
import type { BoardContextValue } from './useBoardContext';
import type { UnitSelection } from './useSelectUnit';
import '../styles/submissions.css';

export interface BoardLectureSuggestionsProps {
  context: BoardContextValue;
  /** Loads the pick as this period's lecture. */
  onSelect: (selection: UnitSelection) => void;
}

// Đề xuất: the lesson this period's teaching plan names, a tap away; the board itself opens blank
export function BoardLectureSuggestions({ context, onSelect }: BoardLectureSuggestionsProps) {
  const tree = useQltIztContentStore((state) => state.tree);
  const loading = useQltIztContentStore((state) => state.loading);
  const loadTree = useQltIztContentStore((state) => state.loadTree);
  const cells = useQltTimetableStore((state) => state.todayCells);
  // a board opened by URL has no route state, so the period's timetable cell names grade and subject
  const scope = resolvePeriodCourseScope({ gradeId: context.gradeId, subjectId: context.subjectId }, cells, context);

  useEffect(() => {
    if (!context.classId || !context.suggestedUnitId) return;
    void loadTree({ class: context.classId, grade: scope.gradeId, subject: scope.subjectId });
  }, [context.classId, context.suggestedUnitId, scope.gradeId, scope.subjectId, loadTree]);

  const rows = useMemo(() => periodSuggestions(tree, { unitId: context.suggestedUnitId, name: context.suggestedUnitName, courseId: context.suggestedCourseId }), [tree, context.suggestedUnitId, context.suggestedUnitName, context.suggestedCourseId]);

  if (rows.length === 0) return <EmptyState icon={<BookOpenIcon size={26} />} title="Chưa có bài đề xuất cho tiết này" description="Kế hoạch giảng dạy chưa gắn bài giảng IZTeach cho tiết học này. Thầy/cô chọn bài trong Kho bài." />;

  return (
    <div className="lecture-library">
      <div className="hw-panel__bar"><p>Bài dạy theo kế hoạch giảng dạy của {context.periodLabel || 'tiết này'}.</p></div>
      {loading && tree.length === 0 ? <Spinner label="Đang tải học liệu" size="small" /> : null}
      <UnitList rows={rows} activeUnitId={context.unitId} onSelect={(row) => onSelect({ unitId: row.unit.id, unitName: stripLeadingOrdinal(row.unit.name), courseId: row.courseId })} />
    </div>
  );
}
